import { directSupabase as supabase } from '../../lib/supabaseClient';
import type { BillPaymentTerms } from './accounts.model';
import {
  fromDbRecord,
  toDbRecord,
  validateRecord,
  type SubcontractorRecord,
} from './subcontractorRecords.model';

function errorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;
  if (error instanceof Error) return error.message;
  const row = error as { message?: string; code?: string };
  if (row.code === '42P01') return 'Subcontractor records are not set up in the database yet. Apply the subcontractor records migration.';
  return row.message || fallback;
}

export const subcontractorRecordsService = {
  async list(): Promise<SubcontractorRecord[]> {
    const { data, error } = await supabase.from('subcontractor_records').select('*');
    if (error) throw new Error(errorMessage(error, 'Failed to load subcontractor records'));
    return (data || []).map(fromDbRecord);
  },

  async save(record: SubcontractorRecord): Promise<SubcontractorRecord> {
    const problem = validateRecord(record);
    if (problem) throw new Error(problem);
    const { data: auth } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('subcontractor_records')
      .upsert({ ...toDbRecord(record), updated_by: auth.user?.id ?? null }, { onConflict: 'account_id' })
      .select('*')
      .single();
    if (error) throw new Error(errorMessage(error, 'Failed to save the subcontractor record'));
    return fromDbRecord(data);
  },

  /** Pay terms live on the account, where new bills read them. Only that column is written. */
  async setPayTerms(accountId: string, terms: BillPaymentTerms): Promise<void> {
    const { error } = await supabase
      .from('directory_accounts')
      .update({ bill_payment_terms: terms })
      .eq('id', accountId);
    if (error) throw new Error(errorMessage(error, 'Failed to save pay terms'));
  },
};
